import { useEffect, useState } from 'react';
import styles from './FloatingLabelInput.module.scss';

/**
 * FloatingLabelInput - iOS-style floating label input component
 *
 * Reusable across entire application for modern form UX
 *
 * @param {Object} props
 * @param {string} props.id - Input ID (required for accessibility)
 * @param {string} props.name - Input name
 * @param {string} props.type - Input type (text, email, password, etc.)
 * @param {string} props.label - Label text
 * @param {string} props.value - Controlled value
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler
 * @param {string} props.placeholder - Optional placeholder (shown when focused)
 * @param {boolean} props.required - Required field
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.error - Error message
 * @param {number} props.maxLength - Max length
 * @param {number} props.minLength - Min length
 * @param {string} props.autoComplete - Autocomplete attribute
 * @param {string} props.className - Additional CSS classes
 */
export const FloatingLabelInput = ({
  id,
  name,
  type = 'text',
  label,
  value = '',
  onChange,
  onBlur,
  placeholder = '',
  required = false,
  disabled = false,
  error = '',
  maxLength,
  minLength,
  autoComplete,
  className = '',
  ...restProps
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  // Check if input has value (for autofill detection)
  useEffect(() => {
    setHasValue(value && value.toString().length > 0);
  }, [value]);

  // Enhanced autofill detection - runs continuously to catch browser autofill
  useEffect(() => {
    const input = document.getElementById(id);
    if (!input) return;

    const checkAutofill = () => {
      try {
        // Check for autofill pseudo-class
        const autofilled = input.matches(':-webkit-autofill') || input.matches(':autofill');

        // Also check if input actually has a value (most reliable)
        const hasActualValue = input.value && input.value.length > 0;

        if (autofilled || hasActualValue) {
          setHasValue(true);
        }
      } catch (e) {
        // Fallback: just check the value directly
        if (input.value && input.value.length > 0) {
          setHasValue(true);
        }
      }
    };

    // Check immediately and after delays (browser autofill can happen at various times)
    checkAutofill();
    const timers = [
      setTimeout(checkAutofill, 100),
      setTimeout(checkAutofill, 300),
      setTimeout(checkAutofill, 500),
      setTimeout(checkAutofill, 1000),
    ];

    // Also check on animation frame (catches autofill that happens during render)
    const rafId = requestAnimationFrame(checkAutofill);

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      cancelAnimationFrame(rafId);
    };
  }, [id]);

  const handleFocus = (e) => {
    setIsFocused(true);
  };

  const handleBlur = (e) => {
    setIsFocused(false);

    // Check if input actually has a value on blur (handles autofill/browser-filled values)
    const actualValue = e.target.value;
    if (actualValue && actualValue.length > 0) {
      setHasValue(true);
    } else {
      setHasValue(false);
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  const isLabelFloating = isFocused || hasValue;

  return (
    <div
      className={`${styles.floatingInputWrapper} ${className} ${error ? styles.hasError : ''} ${disabled ? styles.disabled : ''}`}
    >
      <div className={styles.inputContainer}>
        <input
          id={id}
          name={name || id}
          type={type}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={isFocused ? placeholder : ''}
          required={required}
          disabled={disabled}
          maxLength={maxLength}
          minLength={minLength}
          autoComplete={autoComplete}
          className={styles.input}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${id}-error` : undefined}
          {...restProps}
        />
        <label
          htmlFor={id}
          className={`${styles.label} ${isLabelFloating ? styles.labelFloating : ''}`}
        >
          {label}
          {required && (
            <span className={styles.required} aria-label="required">
              *
            </span>
          )}
        </label>
      </div>
      {error && (
        <div id={`${id}-error`} className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default FloatingLabelInput;
